/**
 * The recipe card views' model of a method (design section 8): ordered stages,
 * each with the ingredient lines that join at it and the instruction steps it
 * covers, plus mise-en-place tasks. Pure, so the views only lay it out.
 */

import type { LineMeasures, RecipeStage, RecipeStageInput } from "@/lib/api/requests/recipes";
import type { RecipeIngredient } from "@/types/recipe";
import type { RecipeStep } from "@/components/recipes/execution-steps";
import type { Uom } from "@/types/material-conversion";

export interface NumberedStep {
  number: number;
  text: string;
  /** The step's photos and videos, as the editor saved them. */
  mediaUrls: string[];
  /** A critical control point (YC-21): the views mark it and the card prints it. */
  isCcp?: boolean;
}

export interface MethodGroup {
  /** Null for what no stage claims yet: lines and steps still to be staged. */
  stage: RecipeStage | null;
  lines: RecipeIngredient[];
  steps: NumberedStep[];
}

export interface StagedMethod {
  tasks: RecipeStage[];
  groups: MethodGroup[];
  /** True when the recipe has stages at all; false means one ungrouped list. */
  isStaged: boolean;
}

/** A step's number is its own `step` when it has one, else its place in the list. */
export function numberedSteps(steps: RecipeStep[]): NumberedStep[] {
  return steps.map((step, i) => ({
    number: typeof step.step === "number" && step.step > 0 ? step.step : i + 1,
    text: step.instruction || step.description || "",
    mediaUrls: step.mediaUrls ?? [],
    // Only when flagged, so a step without one is exactly what it was.
    ...(step.isCcp === true ? { isCcp: true } : {}),
  }));
}

export function buildStagedMethod(
  stages: RecipeStage[],
  lines: RecipeIngredient[],
  steps: RecipeStep[],
): StagedMethod {
  const ordered = [...stages].sort((a, b) => a.sortOrder - b.sortOrder);
  const allSteps = numberedSteps(steps);
  const sortedLines = [...lines].sort((a, b) => a.sortOrder - b.sortOrder);
  const tasks = ordered.filter((s) => s.kind === "TASK");
  const methodStages = ordered.filter((s) => s.kind === "STAGE");

  if (methodStages.length === 0) {
    return { tasks, groups: [{ stage: null, lines: sortedLines, steps: allSteps }], isStaged: false };
  }

  const claimedLines = new Set(methodStages.flatMap((s) => s.ingredientIds));
  // Only method stages show steps, so only they claim them; a step left on a
  // task would otherwise be shown nowhere.
  const claimedSteps = new Set(methodStages.flatMap((s) => s.stepNumbers));
  const groups: MethodGroup[] = methodStages.map((stage) => ({
    stage,
    lines: sortedLines.filter((line) => stage.ingredientIds.includes(line.id)),
    steps: allSteps.filter((step) => stage.stepNumbers.includes(step.number)),
  }));
  const leftover: MethodGroup = {
    stage: null,
    lines: sortedLines.filter((line) => !claimedLines.has(line.id)),
    steps: allSteps.filter((step) => !claimedSteps.has(step.number)),
  };
  if (leftover.lines.length > 0 || leftover.steps.length > 0) groups.push(leftover);
  return { tasks, groups, isStaged: true };
}

/**
 * Where a stage's outcome is held for: the position, in `ordered` (the stages
 * in order), of the later STAGE it flows into, or null when it goes straight
 * into the next stage. A route to a stage that is gone, not later, or not a
 * STAGE is not honoured, so a stale one reads as the default.
 */
export function heldTargetIndex(ordered: RecipeStage[], index: number): number | null {
  const stage = ordered[index];
  if (!stage || stage.kind !== "STAGE" || stage.includeInNext !== false || stage.flowsIntoStageId == null) return null;
  const target = ordered.findIndex((candidate) => candidate.id === stage.flowsIntoStageId);
  return target > index && ordered[target]!.kind === "STAGE" ? target : null;
}

/** The routing fields of a stage's input: nothing for the default, the held route otherwise. */
function heldRouting(ordered: RecipeStage[], index: number): Pick<RecipeStageInput, "includeInNext" | "flowsInto"> {
  const target = heldTargetIndex(ordered, index);
  return target === null ? {} : { includeInNext: false, flowsInto: target };
}

/**
 * The stages again, pointed at the steps' new numbers after the steps were
 * saved: `renumbered` maps a kept step's old number to its new one, and a
 * step that is not in it was deleted, so no stage covers it any more. Null
 * when no stage changes, so nothing needs saving.
 */
export function renumberStageSteps(
  stages: RecipeStage[],
  renumbered: Map<number, number>,
): RecipeStageInput[] | null {
  let changed = false;
  const sorted = [...stages].sort((a, b) => a.sortOrder - b.sortOrder);
  const inputs = sorted.map((stage, index) => {
      const stepNumbers = stage.stepNumbers
        .map((n) => renumbered.get(n))
        .filter((n): n is number => n !== undefined)
        .sort((a, b) => a - b);
      if (stepNumbers.join(",") !== [...stage.stepNumbers].sort((a, b) => a - b).join(",")) changed = true;
      return {
        kind: stage.kind,
        action: stage.action,
        detail: stage.detail,
        stepNumbers,
        ingredientIds: stage.ingredientIds,
        ...heldRouting(sorted, index),
      };
    });
  return changed ? inputs : null;
}

/**
 * "Step 4", "Steps 2–3", or — when the picked steps have a gap — "Steps 2, 4",
 * so a range never implies a step that isn't in the stage. Null for a stage
 * that covers no step.
 */
export function stepRangeLabel(
  stepNumbers: number[],
): { one: number } | { from: number; to: number } | { list: number[] } | null {
  if (stepNumbers.length === 0) return null;
  const sorted = [...new Set(stepNumbers)].sort((a, b) => a - b);
  if (sorted.length === 1) return { one: sorted[0]! };
  const contiguous = sorted.every((n, i) => i === 0 || n === sorted[i - 1]! + 1);
  return contiguous ? { from: sorted[0]!, to: sorted[sorted.length - 1]! } : { list: sorted };
}

const FRACTIONS: [number, string][] = [
  [1 / 8, "1/8"],
  [1 / 4, "1/4"],
  [1 / 3, "1/3"],
  [3 / 8, "3/8"],
  [1 / 2, "1/2"],
  [5 / 8, "5/8"],
  [2 / 3, "2/3"],
  [3 / 4, "3/4"],
  [7 / 8, "7/8"],
];

/**
 * A quantity as it is written on a kitchen sheet: 0.5 → "1/2", 1.25 → "1 1/4",
 * 2 → "2". Anything that is not close to a common fraction keeps up to two
 * decimals.
 */
export function kitchenQuantity(value: number): string {
  if (!Number.isFinite(value)) return "—";
  const whole = Math.floor(value + 1e-9);
  const rest = value - whole;
  if (rest < 0.01) return String(whole);
  if (rest > 0.99) return String(whole + 1);
  const fraction = FRACTIONS.find(([f]) => Math.abs(f - rest) < 0.01);
  if (!fraction) return String(Number(value.toFixed(2)));
  return whole > 0 ? `${whole} ${fraction[1]}` : fraction[1];
}

const PLURAL_UNITS = /^(cups|qts|lbs|pts|gals|tbsps|tsps|ozs|cans|bags|cases)$/i;

/**
 * A scaled quantity with its unit: "1/2 cup", "2 qts". A line written as
 * "2 qts" halves to "1 qt", not "1 qts".
 */
export function kitchenAmount(quantity: number, unit: string): string {
  const amount = kitchenQuantity(quantity);
  const singular = quantity <= 1 + 1e-9 && PLURAL_UNITS.test(unit.trim()) ? unit.trim().slice(0, -1) : unit;
  return `${amount} ${singular}`.trim();
}

/**
 * How many times the recipe as written a batch makes: 0.5 is a half batch, 1
 * the recipe, 10 ten of it, 2.5 anything the cook types.
 */
export type BatchScale = number;

/** The scales the card offers as one tap; anything else is typed. */
export const BATCH_PRESETS: readonly BatchScale[] = [0.5, 1, 5, 10];

/** A typo such as 1000000 should not print a card for a million batches. */
export const MAX_BATCH_SCALE = 1000;

/** A scale the card can use: a number above zero and not absurd. */
export function isValidScale(value: number): boolean {
  return Number.isFinite(value) && value > 0 && value <= MAX_BATCH_SCALE;
}

/** What the cook typed as a number, or null when it is not a usable one. */
export function parseAmount(text: string): number | null {
  const value = Number(text.trim().replace(",", "."));
  return text.trim() !== "" && Number.isFinite(value) && value > 0 ? value : null;
}

/** "2×", "1/2×" — a multiple of the recipe as written. */
export function multipleLabel(scale: BatchScale): string {
  return `${kitchenQuantity(scale)}×`;
}

/** The yield at a scale, "16" for half of 32; null when the recipe has no yield. */
export function scaledYield(yieldQuantity: number | null | undefined, scale: BatchScale): string | null {
  if (yieldQuantity == null || !(Number(yieldQuantity) > 0)) return null;
  return kitchenQuantity(Number(yieldQuantity) * scale);
}

/**
 * The scale that makes `target` of a recipe that yields `yieldQuantity` — both
 * in the recipe's own yield unit, which is why the card only asks for the
 * target in that unit. Null when either is unusable or the answer is out of range.
 */
export function scaleForYield(target: number, yieldQuantity: number | null | undefined): BatchScale | null {
  if (yieldQuantity == null || !(Number(yieldQuantity) > 0) || !(target > 0)) return null;
  const scale = target / Number(yieldQuantity);
  return isValidScale(scale) ? scale : null;
}

/**
 * The two amounts the staged sheet sets side by side: the recipe's half and
 * full batch, or — when the cook has picked another scale — the recipe as
 * written beside that scale. The second is the one in bold.
 */
export function sheetScales(scale: BatchScale): [BatchScale, BatchScale] {
  return scale === 0.5 || scale === 1 ? [0.5, 1] : [1, scale];
}

export interface FlowRow {
  line: RecipeIngredient;
  /** Index of the stage the line joins at, or null when no stage claims it. */
  joinsAt: number | null;
}

/** A held stage's outcome arriving at the stage it flows into. */
export interface HeldInflow {
  /** Index of the stage the outcome comes from. */
  column: number;
  stage: RecipeStage;
}

export interface FlowLayout {
  /** The method's stages in order — one column each. */
  columns: MethodGroup[];
  /** Lines in the order they join, unstaged ones last. */
  rows: FlowRow[];
  /**
   * How many rows each stage works on: every line that has reached it, from
   * its own ingredients and the outcomes flowing into it. At least one, so a
   * stage that adds nothing still shows.
   */
  spans: number[];
  /** The row, from the top, each stage's block starts at; 0 unless a held outcome skips it. */
  starts: number[];
  /** Per stage, the held outcomes that are added at it ("add when ready"). */
  heldInto: HeldInflow[][];
}

/**
 * The flow grid's layout (designs 8b and the tabular card): a stage works on
 * every line that reached it, so its block spans the rows from the first to
 * the last of them. A stage's outcome goes into the next stage unless it is
 * held, in which case it skips the stages in between and is added at the one
 * it flows into. Without held stages that is every line joined so far, from
 * the top row, exactly as before.
 */
export function flowLayout(method: StagedMethod): FlowLayout {
  const columns = method.groups.filter((group) => group.stage !== null);
  const rows: FlowRow[] = [];
  columns.forEach((group, index) => group.lines.forEach((line) => rows.push({ line, joinsAt: index })));
  method.groups
    .filter((group) => group.stage === null)
    .forEach((group) => group.lines.forEach((line) => rows.push({ line, joinsAt: null })));

  const stages = columns.map((group) => group.stage!);
  const reached: Set<number>[] = columns.map(() => new Set<number>());
  const heldInto: HeldInflow[][] = columns.map(() => []);
  let offset = 0;
  columns.forEach((group, index) => {
    group.lines.forEach((_, i) => reached[index]!.add(offset + i));
    offset += group.lines.length;
  });
  columns.forEach((group, index) => {
    if (index === columns.length - 1) return;
    const held = heldTargetIndex(stages, index);
    const target = held ?? index + 1;
    reached[index]!.forEach((row) => reached[target]!.add(row));
    if (held !== null) heldInto[held]!.push({ column: index, stage: group.stage! });
  });

  const starts = reached.map((set) => (set.size === 0 ? 0 : Math.min(...set)));
  const spans = reached.map((set, index) => (set.size === 0 ? 1 : Math.max(...set) - starts[index]! + 1));
  return { columns, rows, spans, starts, heldInto };
}

/** The `uoms` row a line's unit names, however it was spelled ("tsp.", "cups", "LBS"). */
export function findUomFor(unit: string, uoms: Uom[]): Uom | null {
  const cleaned = unit.trim().toLowerCase().replace(/\./g, "").replace(/\s+/g, " ");
  if (!cleaned) return null;
  const candidates = [cleaned, cleaned.replace(/\s/g, ""), cleaned.endsWith("s") ? cleaned.slice(0, -1) : null].filter(
    (c): c is string => !!c,
  );
  return (
    uoms.find((u) =>
      candidates.some((c) => u.code === c || u.label === c || u.aliases.includes(c.toUpperCase())),
    ) ?? null
  );
}

/**
 * The metric equivalent of a US measure, for cards that print both
 * ("4 oz (113 g)"). Null for metric and count units, and for units the table
 * does not know — a weight is never guessed from a volume.
 */
export function metricEquivalent(quantity: number, unit: string, uoms: Uom[]): string | null {
  const uom = findUomFor(unit, uoms);
  if (!uom || uom.system !== "US" || uom.type === "EACH") return null;
  const base = quantity * uom.toBase;
  const amount = base < 10 ? String(Number(base.toFixed(1))) : String(Math.round(base));
  return `${amount} ${uom.type === "WEIGHT" ? "g" : "mL"}`;
}

/** Grams as a card prints them: "2.5 g", "115 g", "1.25 kg". */
export function formatGrams(grams: number): string {
  if (grams >= 1000) return `${Number((grams / 1000).toFixed(2))} kg`;
  return `${grams < 10 ? Number(grams.toFixed(1)) : Math.round(grams)} g`;
}

/** Millilitres as a card prints them: "2.5 mL", "237 mL", "1.89 L". */
export function formatMillilitres(ml: number): string {
  if (ml >= 1000) return `${Number((ml / 1000).toFixed(2))} L`;
  return `${ml < 10 ? Number(ml.toFixed(1)) : Math.round(ml)} mL`;
}

const CUP_ML = 236.5882365;
const TBSP_ML = 14.78676478125;
const TSP_ML = 4.92892159375;

/**
 * Millilitres in the spoon or cup a cook would reach for: "3/4 cup",
 * "1 1/2 Tbsp", "1/4 tsp". Cups from a quarter cup up, then tablespoons, then
 * teaspoons, each rounded to a fraction a measuring set has.
 */
export function kitchenVolume(ml: number): string {
  const [size, unit, step] = ml >= CUP_ML / 4 ? [CUP_ML, "cups", 8] : ml >= TBSP_ML ? [TBSP_ML, "Tbsp", 2] : [TSP_ML, "tsp", 8];
  const amount = Math.max(1 / step, Math.round((ml / size) * step) / step);
  return kitchenAmount(amount, unit);
}

/**
 * What a card prints in brackets after an amount. A cup of sugar or two eggs
 * shows what it weighs, when the ingredient's supplier item gives a density or
 * a case both counted and weighed — "1 cup (200 g)". Otherwise a US measure
 * shows its metric equivalent, and a metric one shows nothing. A weighed or
 * counted line whose ingredient has a density also shows the volume it fills —
 * "8 oz (227 g · 1 3/4 cups)", "250 g (1 cup)".
 */
export function cardEquivalent(
  quantity: number,
  unit: string,
  measures: LineMeasures | null | undefined,
  scale: number,
  uoms: Uom[] | undefined,
): string | null {
  const weight =
    measures && measures.family !== "weight" && measures.grams !== null
      ? formatGrams(measures.grams * scale)
      : uoms
        ? metricEquivalent(quantity * scale, unit, uoms)
        : null;
  const volume =
    measures && measures.family !== "volume" && measures.millilitres !== null && measures.millilitres > 0
      ? kitchenVolume(measures.millilitres * scale)
      : null;
  return [weight, volume].filter((part): part is string => part !== null).join(" · ") || null;
}
